import './AppNameInput.scss';

import { AntdOptionType } from '@sonar-software/sonar-ts-types';
import { AutoComplete } from 'antd';
import { BaseSelectRef } from 'rc-select';
import { caseInsensitiveContainsFilterOption } from '@lib/filter';
import { DataSecurityAppName } from '@ui/applications/DataSecurityAppNames';
import { getDirectParent } from '@lib/popups';
import { haltKeyboardEventPropagation } from '@lib/events';
import { PLACEHOLDERS_COPY } from '@sonar-software/sonar-ts-constants';
import { useAsyncDefaultState } from '@hooks';

import React, { forwardRef, useCallback, useMemo, useRef, useState } from 'react';

export const defaultAppOptions: AntdOptionType[] = (Object.values(DataSecurityAppName) as Array<DataSecurityAppName>)
	.map((value) => ({
		label: value,
		value
	}));

export interface AppNameInputProps {
	defaultOpen?: boolean;
	getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
	onChange?: (value: string) => void
	onClose?: () => void;
	onOpen?: () => void;
	onSelect?: (value: string) => void,
	value?: string;
}

export const AppNameInput = forwardRef<BaseSelectRef, AppNameInputProps>(({
	defaultOpen,
	getPopupContainer,
	onChange,
	onClose,
	onOpen,
	onSelect,
	value
}, ref) => {
	const [ searchText, setSearchText ] = useState('');
	const valueBackupRef = useRef(value);
	const [ selectedValue, setSelectedValue ] = useAsyncDefaultState(
		value,
		(initalizedValue) => {
			valueBackupRef.current = initalizedValue;
		}
	);

	const [ optionMatch ] = useMemo(
		() => defaultAppOptions.filter((option) => String(option.value)?.toLowerCase() === searchText?.toLowerCase()),
		[ searchText ]
	);

	const showCustomOption = Boolean(searchText) && !optionMatch;

	const options = useMemo(() => {
		const label = `Create New App "${searchText}"`;

		if (!showCustomOption) {
			return defaultAppOptions;
		}

		return [
			...defaultAppOptions,
			{
				className: 'custom-app-option',
				label,
				value: searchText
			}
		];
	}, [ searchText, showCustomOption ]);

	const handleOnChange = useCallback((input: string) => {
		setSearchText('');
		setSelectedValue(input);
		valueBackupRef.current = input;

		if (onChange) {
			onChange(input);
		}
	}, [ onChange, setSelectedValue ]);

	const handleOnSearch = useCallback((input: string) => {
		setSearchText(input);
	}, []);

	const handleDropdownChange = useCallback((open: boolean) => {
		if (open) {
			setSelectedValue('');

			if (onOpen) {
				onOpen();
			}
		} else if (!open && onClose) {
			onClose();
		}
	}, [ onClose, onOpen, setSelectedValue ]);

	const handleOnBlur = useCallback(() => {
		if (!selectedValue) {
			setSelectedValue(valueBackupRef.current ?? '');
		}

		if (onClose) {
			onClose();
		}
	}, [ onClose, selectedValue, setSelectedValue ]);

	return (
		<AutoComplete
			className='app-name-input'
			classNames={ {
				popup: {
					root: 'app-name-input-dropdown'
				}
			} }
			defaultActiveFirstOption={ !showCustomOption }
			defaultOpen={ defaultOpen }
			filterOption={ caseInsensitiveContainsFilterOption }
			getPopupContainer={ getPopupContainer ?? getDirectParent }
			onBlur={ handleOnBlur }
			onChange={ handleOnChange }
			onFocus={ () => setSelectedValue('') }
			onKeyUp={ haltKeyboardEventPropagation }
			onOpenChange={ handleDropdownChange }
			onSearch={ handleOnSearch }
			onSelect={ onSelect }
			options={ options }
			placeholder={ valueBackupRef.current ?? PLACEHOLDERS_COPY.APP_NAME }
			ref={ ref }
			showAction={ [ 'focus', 'click' ] }
			value={ selectedValue } />
	);
});
